import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import CitizenMapView from '@containers/common/CitizenMapView/CitizenMapView';
import { useAuthManagerContext } from '@private-crm-app/auth-manager-context';
import PrivateSubmissionLayout from '@private-crm-app/components/PrivateSubmissionLayout/PrivateSubmissionLayout';
import { PrivateSubmissionAppRoutes } from '@private-crm-app/constants';
import { getCRMTokenFromUrlOrStorage, trackPublicMapSubmitRequestClicked } from '@private-crm-app/utils';

import './style.scss';

export default function PublicMapPage() {
  const location = useLocation();
  const history = useHistory();
  const { isAuthenticated } = useAuthManagerContext();
  const crmToken = useMemo(() => {
    const param = new URLSearchParams(location.search).get('crm_token');
    return param !== null ? param : getCRMTokenFromUrlOrStorage(location.search);
  }, [location.search]);

  const createUrl = crmToken
    ? `${PrivateSubmissionAppRoutes.CREATE}?crm_token=${encodeURIComponent(crmToken)}`
    : PrivateSubmissionAppRoutes.CREATE;

  const handleMapConfig404 = useCallback(() => {
    history.push(PrivateSubmissionAppRoutes.ERROR);
  }, [history]);

  useEffect(() => {
    if (!crmToken) {
      history.replace(PrivateSubmissionAppRoutes.MISSING_TOKEN_ERROR);
      return;
    }
    // Ensure token is in URL for shareability — update URL if token came from storage only
    const urlToken = new URLSearchParams(location.search).get('crm_token');
    if (!urlToken) {
      history.replace({
        pathname: location.pathname,
        search: `?crm_token=${encodeURIComponent(crmToken)}`,
      });
    }
  }, [crmToken, history, location.pathname, location.search]);

  if (!crmToken) {
    return null;
  }

  return (
    <PrivateSubmissionLayout mainPadding={false}>
      <div className="public-map-page">
        <h1 className="sr-only">Public Map</h1>
        <section className="public-map-page__map" aria-label="Public map">
          <CitizenMapView crmToken={crmToken} onMapConfig404={handleMapConfig404} />
        </section>
        <section className="public-map-page__cta" aria-labelledby="public-map-cta-heading">
          <h2 id="public-map-cta-heading" className="public-map-page__cta-heading">
            Don&apos;t see your request?
          </h2>
          <Link
            to={createUrl}
            className="public-map-page__cta-btn"
            aria-label="Submit a new request"
            onClick={() => trackPublicMapSubmitRequestClicked(isAuthenticated)}
          >
            Submit Request
          </Link>
        </section>
      </div>
    </PrivateSubmissionLayout>
  );
}
